import { format as formatDateFns, isValid, parse } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

export const DISPLAY_DATE_PATTERN = 'dd-MM-yyyy';
export const DISPLAY_DATE_TIME_PATTERN = `${DISPLAY_DATE_PATTERN} hh:mm a`;

const DEFAULT_PATTERN = DISPLAY_DATE_TIME_PATTERN;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DISPLAY_DATE_REGEX = /^(\d{2})-(\d{2})-(\d{4})$/;
const DISPLAY_DATE_SLASH_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})$/;

const parseStrictDate = (value, pattern) => {
  const parsed = parse(value, pattern, new Date());
  if (!isValid(parsed)) return null;
  return formatDateFns(parsed, pattern) === value ? parsed : null;
};

export const parseDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (ISO_DATE_PATTERN.test(trimmed)) {
      return parseStrictDate(trimmed, 'yyyy-MM-dd');
    }

    const normalizedDisplayDate = trimmed.replace(/\//g, '-');
    if (DISPLAY_DATE_REGEX.test(normalizedDisplayDate)) {
      return parseStrictDate(normalizedDisplayDate, DISPLAY_DATE_PATTERN);
    }

    const nativeParsed = new Date(trimmed);
    return isValid(nativeParsed) ? nativeParsed : null;
  }

  const nativeParsed = new Date(value);
  return isValid(nativeParsed) ? nativeParsed : null;
};

export const toApiDateValue = (value) => {
  if (!value) return '';

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (ISO_DATE_PATTERN.test(trimmed)) {
      return trimmed;
    }
    const normalizedDisplayDate = trimmed.replace(/\//g, '-');
    if (DISPLAY_DATE_REGEX.test(normalizedDisplayDate)) {
      const parsed = parseStrictDate(normalizedDisplayDate, DISPLAY_DATE_PATTERN);
      return parsed ? formatDateFns(parsed, 'yyyy-MM-dd') : '';
    }
  }

  const parsed = parseDateValue(value);
  return parsed ? formatDateFns(parsed, 'yyyy-MM-dd') : '';
};

export const toDisplayDateValue = (value, timeZone = 'Australia/Melbourne') => {
  if (!value) return '';

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';

    if (DISPLAY_DATE_REGEX.test(trimmed)) {
      return trimmed;
    }

    if (DISPLAY_DATE_SLASH_REGEX.test(trimmed)) {
      return trimmed.replace(/\//g, '-');
    }

    if (ISO_DATE_PATTERN.test(trimmed)) {
      const parsed = parseStrictDate(trimmed, 'yyyy-MM-dd');
      return parsed ? formatDateFns(parsed, DISPLAY_DATE_PATTERN) : '';
    }
  }

  return formatInStoreTimezone(value, DISPLAY_DATE_PATTERN, timeZone);
};

export const formatInStoreTimezone = (dateInput, pattern = DEFAULT_PATTERN, timeZone = 'Australia/Melbourne') => {
  if (!dateInput) return '';
  try {
    return formatInTimeZone(dateInput, timeZone, pattern);
  } catch (err) {
    try {
      return formatDateFns(dateInput, pattern);
    } catch {
      return '';
    }
  }
};

export const toLocalDateString = (dateInput, timeZone = 'Australia/Melbourne') => {
  return toDisplayDateValue(dateInput, timeZone);
};

export const toLocalDateTimeString = (dateInput, timeZone = 'Australia/Melbourne') => {
  return formatInStoreTimezone(dateInput, DEFAULT_PATTERN, timeZone);
};
